/*─────────────────────────────────────────────────────────────────────────────
 * App.tsx
 *
 * Root application component. Responsibilities:
 *   1. Initialize auth, theme, and i18n stores on mount
 *   2. Render public routes (login, setup) when unauthenticated
 *   3. Render protected layout with all module routes when authenticated
 *
 * Route organisation — each group lives in its own file under src/routes/:
 *   - SystemRoutes   → users, roles, appearance, integrations, telephony infra
 *   - AdminRoutes    → AI, firewall, network, maintenance, licensing, apps SSO
 *   - PbxRoutes      → extensions, routing, call center, applications, tools…
 *   - ReportsRoutes  → CDR, active calls, IVR stats, system logs
 *─────────────────────────────────────────────────────────────────────────────*/

import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './store/auth';
import { useThemeStore } from './store/theme';
import { useI18n } from './hooks/useI18n';
import { moduleIdsService } from './services/moduleIds';
import { toast } from 'sonner';

// Layout & shell components
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationProvider } from './contexts/NotificationContext';
import { SessionExpiredOverlay } from './components/auth/SessionExpiredOverlay';
import { SessionWarningModal } from './components/auth/SessionWarningModal';
import { StartModuleRedirect } from './components/StartModuleRedirect';

// Pages that live outside module folders
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import DashboardPage from './pages/dashboards/DashboardPage';

// Route groups (one file per menu section)
import { SystemRoutes } from './routes/SystemRoutes';
import { AdminRoutes } from './routes/AdminRoutes';
import { PbxRoutes } from './routes/PbxRoutes';
import { ReportsRoutes } from './routes/ReportsRoutes';

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

import { Loader2 } from 'lucide-react';
import { api } from './lib/api';
import { useContextStore } from './store/context';

const LoginRedirect = () => {
  const { user } = useAuthStore();
  const { selectedDomain } = useContextStore();
  const params = new URLSearchParams(window.location.search);
  const callback = params.get('callback');
  const appTarget = params.get('app');

  useEffect(() => {
    if (!callback) return;

    if (appTarget === 'switchboard') {
      const hasAccess = (user as any)?.appPrefs?.switchboardEnabled ?? true;
      if (!hasAccess) {
        setTimeout(() => {
          toast.error('Acceso Denegado: No tienes permiso para acceder al Switchboard');
        }, 100);
        window.location.replace('/dashboard');
        return;
      }
    }

    const processRedirect = async () => {
      try {
        let endpoint = '';
        if (appTarget === 'switchboard') endpoint = '/api/apps/switchboard/sso-token';
        else if (appTarget === 'portal') endpoint = '/api/apps/portal/sso-token';

        let ssoToken = '';
        if (endpoint) {
          // Provide domainId to satisfy backend validation
          const domainId = selectedDomain?.id || 1;
          const res = await api.post(endpoint, { domainId });
          ssoToken = res.data.token;
        } else {
          // Fallback to standard platform token for billing/kamailio
          ssoToken = localStorage.getItem('auth_token') || '';
        }

        const redirectUrl = new URL(decodeURIComponent(callback));
        if (ssoToken) {
          redirectUrl.searchParams.set('sso_token', ssoToken);
        }
        window.location.replace(redirectUrl.toString());

      } catch (err: any) {
        console.error('SSO Token error:', err.response?.data || err);
        toast.error('SSO Generation failed');
        window.location.replace('/dashboard');
      }
    };

    processRedirect();
  }, [callback, appTarget, user, selectedDomain]);

  if (!callback) return <Navigate to="/dashboard" replace />;
  
  return (
    <div className="flex h-[80vh] w-full items-center justify-center bg-slate-900 border-none">
      <div className="text-center p-8 bg-slate-800 rounded-xl border border-slate-700 max-w-sm mx-auto shadow-2xl">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
        <p className="text-lg text-white font-semibold">Generando sesión SSO...</p>
        <p className="text-sm text-slate-400 mt-2">Conectando con {appTarget || 'aplicación'}</p>
      </div>
    </div>
  );
};


function App() {
  const { isAuthenticated, isInitializing, initializeAuth } = useAuthStore();
  const { initializeTheme } = useThemeStore();
  const { initializeI18n } = useI18n();

  // Initialize auth (async — verifies session with backend) and i18n exactly once on mount
  useEffect(() => {
    initializeAuth();   // returns a Promise; let it run
    initializeI18n();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load theme and module IDs whenever auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      // Load theme preferences from the database for the logged-in user
      initializeTheme();

      // Pre-fetch module IDs used by ProtectedRoute permission checks
      moduleIdsService.loadModuleIds().catch((error) => {
        console.error('Failed to load module IDs:', error);
      });
    }
  }, [isAuthenticated]); // intentionally excludes initializeTheme to prevent loops

  // ── Session verification in progress — block ALL route rendering ────────
  if (isInitializing) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // ── Unauthenticated shell — only login & setup are accessible ──────────
  if (!isAuthenticated) {
    return (
      <ErrorBoundary>
        <SessionExpiredOverlay />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/setup" element={<SetupPage />} />
          {/* Redirect any other path to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  // ── Authenticated shell — full layout with sidebar & all module routes ──
  return (
    <NotificationProvider>
      <ErrorBoundary>
        <SessionExpiredOverlay />
        <SessionWarningModal />
        <Layout>
          <Routes>
            {/* Core navigation */}
            <Route path="/" element={<StartModuleRedirect />} />
            <Route path="/login" element={<LoginRedirect />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Module route groups */}
            {SystemRoutes()}
            {AdminRoutes()}
            {PbxRoutes()}
            {ReportsRoutes()}

            {/* Fallback — redirect unknown paths to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Layout>
      </ErrorBoundary>
    </NotificationProvider>
  );
}

export default App;
